import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import SimpleDrawer, {
  SimpleDrawerAction,
} from '../../../shared/simple-drawer-new';
import { getAllDecomModels, UserKeys } from '../../../api/users-api';
import { useDecomPolicyActions } from './hooks/use-decom-policy-actions';
import ReviewStep from './decom-policy-form/form-steps/review-step';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../utils/roles/client-roles';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';

import Typography from '@mui/material/Typography';
import Edit from '@mui/icons-material/Edit';
import LibraryAdd from '@mui/icons-material/LibraryAdd';

export default function DecomPolicyDrawerDetails() {
  const { toolsRoles } = useUserSettings();
  const [searchParams] = useSearchParams();
  const selectedPolicyId = searchParams.get('selectedDecomPolicy');
  const { closeDrawer, handleEditPolicy, handleClonePolicy } =
    useDecomPolicyActions();

  const { data: decomModels, isLoading } = useQuery({
    queryKey: [UserKeys.DecomModels],
    queryFn: () => getAllDecomModels(),
    enabled: !!selectedPolicyId,
  });

  const selectedPolicy = Array.isArray(decomModels)
    ? decomModels.find((m) => m.id === selectedPolicyId) ?? null
    : null;
  const canEditDecom = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.EditDecommissioningProcesses.value
  );
  const canCloneDecom = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.CreateDecommissioningProcesses.value
  );

  const editDisabledReason = canEditDecom
    ? undefined
    : Messages.Errors.Permission.Acting(
        ToolsRoles.EditDecommissioningProcesses.name
      );
  const cloneDisabledReason = canCloneDecom
    ? undefined
    : Messages.Errors.Permission.Acting(
        ToolsRoles.CreateDecommissioningProcesses.name
      );

  const actionsMenu: SimpleDrawerAction[] | undefined = selectedPolicy
    ? [
        {
          label: 'Edit',
          icon: <Edit fontSize="small" />,
          onClick: () => handleEditPolicy(selectedPolicy),
          disabled: !canEditDecom,
          disabledReason: editDisabledReason,
        },
        {
          label: 'Clone',
          icon: <LibraryAdd fontSize="small" />,
          onClick: () => handleClonePolicy(selectedPolicy),
          disabled: !canCloneDecom,
          disabledReason: cloneDisabledReason,
        },
      ]
    : undefined;

  return (
    <SimpleDrawer
      title="Decommission Policy Details"
      isLoading={isLoading}
      open={!!selectedPolicyId}
      onClose={closeDrawer}
      actionsMenu={actionsMenu}
    >
      {selectedPolicy ? (
        <ReviewStep selectedPolicy={selectedPolicy} isDrawer />
      ) : (
        <Typography sx={{ p: 3 }} color="error">
          Policy not found.
        </Typography>
      )}
    </SimpleDrawer>
  );
}
